import React from 'react';
import { Badge } from '@savvy-kit/components/badge/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@savvy-kit/components/tooltip';

import type { TabsListVariant } from '../../components/tabs/tabs';
import { TabsList, Tabs as TabsPrimitive, TabsTrigger } from '../../components/tabs/tabs';

type TabCommon = {
  readonly value: string;
  readonly disabled?: boolean;
  readonly disabledTooltip?: string;
  /** Draws a divider before this tab, so a flat list can read as groups. */
  readonly separatorBefore?: boolean;
};

/**
 * A tab is described either by a `name` the trigger renders as its content, or by a `render` element
 * the trigger *becomes*. Modelled as a union rather than two optional fields so a tab can't be
 * declared with neither and render blank, and so `showBadge` can't be set on a `render` tab, where
 * the element supplies its own children and the badge would be silently dropped.
 */
export type Tab = TabCommon &
  (
    | {
        readonly name: React.ReactNode;
        readonly showBadge?: boolean;
        readonly render?: never;
      }
    | {
        /**
         * Render the tab *as* this element rather than nesting content inside the trigger's own
         * button — pass a router `Link` to make the tab an anchor. Base UI clones the element with
         * the trigger's props, so it has to forward everything it receives to a real DOM node, and
         * it supplies its own content.
         */
        readonly render: React.ReactElement;
        readonly name?: never;
        readonly showBadge?: never;
      }
  );

export type TabGroupProps = {
  readonly variant?: TabsListVariant;
  readonly activeTab?: string;
  readonly setActiveTab?: (tab: string) => void;
  readonly tabs: Tab[];
  readonly className?: string;
  /** Let the tabs break onto another row instead of overflowing their container. */
  readonly wrap?: boolean;
  /**
   * Accessible name for the underlying `role="tablist"`. WAI-ARIA APG requires
   * a tablist to have an accessible name when there is no visible label
   * adjacent to the control — pass this whenever the surrounding form has no
   * visible "what is this group" heading.
   */
  readonly 'aria-label'?: string;
  /**
   * Same purpose as `aria-label` but referencing an existing element id —
   * use when the surrounding form already renders a visible label.
   */
  readonly 'aria-labelledby'?: string;
};

function TabGroup({
  variant,
  activeTab,
  setActiveTab,
  tabs,
  wrap,
  className,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
}: TabGroupProps) {
  const renderTrigger = (tab: Tab) => {
    if (tab.render) {
      return <TabsTrigger key={tab.value} value={tab.value} disabled={tab.disabled} render={tab.render} />;
    }
    return (
      <TabsTrigger key={tab.value} value={tab.value} disabled={tab.disabled}>
        {tab.name}
        {tab.showBadge && <Badge variant="primary" type="dot" />}
      </TabsTrigger>
    );
  };

  return (
    <TabsPrimitive value={activeTab} onValueChange={setActiveTab} className="gap-4">
      <TabsList
        variant={variant}
        wrap={wrap}
        className={className}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
      >
        {tabs.map((tab) => (
          <React.Fragment key={tab.value}>
            {tab.separatorBefore && (
              <span aria-hidden className="mx-1.5 h-5 w-px shrink-0 self-center bg-border-light" />
            )}
            {tab.disabled && tab.disabledTooltip ? (
              // The tooltip renders *as* the tab rather than wrapping it in a hoverable span. A
              // wrapper would be the flex item instead of the trigger, and `flex-basis: 0%` adds
              // each item's padding on top of its share — so a padding-less wrapper came out
              // narrower than its `px-3` siblings whenever a caller passed `*:flex-1`.
              // Rendering the same element `renderTrigger` builds, rather than rebuilding it here,
              // is what keeps `render` and `showBadge` working on a disabled tab.
              <Tooltip>
                <TooltipTrigger render={renderTrigger(tab)} />
                <TooltipContent side="bottom">
                  <p>{tab.disabledTooltip}</p>
                </TooltipContent>
              </Tooltip>
            ) : (
              renderTrigger(tab)
            )}
          </React.Fragment>
        ))}
      </TabsList>
    </TabsPrimitive>
  );
}

export default TabGroup;
